/**
 * BV/Curalate media rendition helpers.
 *
 * A UGC media object carries several renditions of the same asset (`original`,
 * `extraLarge`, `large`, `small`, `mediumSquare`, …). Photos use the image
 * renditions directly; videos put the video file on `original`/`highQuality`/
 * `lowQuality` and the still frame on `poster`.
 */

/**
 * Rendition keys ordered highest-fidelity first. `original` is the full-size
 * asset, so it always wins when present; the rest are a safe fallback chain.
 */
export const UGC_RENDITION_PREFERENCE = [
    'original',
    'extraLarge',
    'large',
    'highQuality',
    'medium',
    'small',
    'lowQuality'
]

/**
 * First available rendition link on a Curalate media (or poster) object —
 * `original` first, then the next best rendition. For a video this returns the
 * video file; for its `poster` it returns the still image.
 *
 * @param {Object} source - a media object or a `media.poster` object
 * @returns {string} rendition URL, or '' when none is available
 */
export const getUgcRenditionLink = (source) => {
    for (const key of UGC_RENDITION_PREFERENCE) {
        const variant = source?.[key]
        if (typeof variant === 'string') return variant
        if (variant?.link) return variant.link
    }
    return ''
}

/**
 * Image URL for a media item rendered as an <img> (thumbnails). Video items
 * use their poster, because their `original`/`highQuality` renditions are the
 * video itself and can't be rendered as an image.
 *
 * @param {Object} media - `mediaItem.media`
 * @returns {string}
 */
export const getUgcThumbnailLink = (media) => {
    if (media?.type === 'video') {
        return getUgcRenditionLink(media?.poster) || getUgcRenditionLink(media)
    }
    return getUgcRenditionLink(media) || getUgcRenditionLink(media?.poster)
}

/**
 * Rendition keys for a video element, best-streaming first. Unlike an image,
 * a video's `original` is the full-size file, so `highQuality` is preferred —
 * otherwise the desktop autoplay pulls the largest file for every look.
 */
export const UGC_VIDEO_RENDITION_PREFERENCE = ['highQuality', 'original', 'medium', 'lowQuality']

/**
 * First available rendition link for a video source, preferring a streamable
 * `highQuality` over the full-size `original`.
 *
 * @param {Object} source - a video media object
 * @returns {string} rendition URL, or '' when none is available
 */
export const getUgcVideoRenditionLink = (source) => {
    for (const key of UGC_VIDEO_RENDITION_PREFERENCE) {
        const variant = source?.[key]
        if (typeof variant === 'string') return variant
        if (variant?.link) return variant.link
    }
    return ''
}

/**
 * Ordered, de-duplicated look ids for mobile swipe navigation.
 *
 * Uses the landing-feed order. If the currently-open look isn't in the loaded
 * feed, it is seeded first so swiping still moves through the rest of the feed.
 *
 * @param {Object} args
 * @param {Array} [args.feedItems] - landing-feed list items ({id, ...})
 * @param {string} [args.activeId] - currently-open look id
 * @returns {string[]}
 */
export const buildUgcDeck = ({feedItems = [], activeId} = {}) => {
    const ids = [...new Set(feedItems.map((item) => item?.id).filter(Boolean))]
    if (!activeId || ids.includes(activeId)) return ids
    return [activeId, ...ids]
}

/**
 * Wraps an index into `[0, length)` so the mobile swipe deck loops — swiping
 * past the last look returns to the first and swiping before the first returns
 * to the last.
 *
 * @param {number} index
 * @param {number} length
 * @returns {number} index in range, or -1 when the deck is empty
 */
export const cycleIndex = (index, length) => {
    if (!length || length < 1) return -1
    return ((index % length) + length) % length
}
